import React from 'react';
import {observable, action, computed, makeObservable} from 'mobx';
import {observer} from 'mobx-react';
import store from '../../s';
import {checkout} from '../../../api';
import Confidence from '../confidence';
import ModalPromo from '../modals/promo';
import CartItems from './cart-items';
import * as emitter from '../../../global/emitter';
import {hideAllModals} from '../../../util';
import {ICheckoutStep, ICheckoutType} from '../../i';
import {getCartItemTotal} from './cart-quantity/cart-quantity';
import MobileHeader from './components/cart-summary/mobile-header';
import Header from './components/cart-summary/header';
import Totals, {TotalRow} from './components/cart-summary/totals';
import Promo from './components/cart-summary/promo';
import CheckoutButtons from './components/cart-summary/checkout-buttons';
import PayLater from './components/cart-summary/pay-later';
import MobileFooter from './components/cart-summary/mobile-footer';
import EmptyCart from './components/cart-summary/empty-cart';
import SummaryRedesign from './components/cart-summary/ab-redesign-summary';
import {isCheckoutRedesign} from '../../ab-redesign';

declare const collapse: {
    init: () => void;
};

interface ICartSummaryProps {
    isMobile: boolean;
    /** Redesign, stacked layout, steps after the cart: the full summary rendered a second time below the step cards. */
    stackedDetail?: boolean;
}

@observer
class CartSummaryComponent extends React.Component<ICartSummaryProps> {
    @observable randInt: number;
    @observable editingPromo: boolean;

    constructor(props) {
        super(props);
        this.randInt = Math.floor(Math.random() * 1000000001);
        this.handleCheckoutTypeChange = this.handleCheckoutTypeChange.bind(this);
        this.resetCart = this.resetCart.bind(this);
        this.reloadPayment = this.reloadPayment.bind(this);
        this.reloadPaymentFailure = this.reloadPaymentFailure.bind(this);
        this.refreshStuff = this.refreshStuff.bind(this);
        this.saveEstPostal = this.saveEstPostal.bind(this);
        this.editingPromo = false;
        makeObservable(this);
    }

    abForceUpdate = async () => this.forceUpdate();

    componentDidMount(): void {
        collapse.init();
        // The redesign's stacked detail copy observes the same store and reads no AB flags, so it stays off the store hook
        // and the shared 'ab' subscription: checkout.tsx dispatches by that fixed key, and unsubscribing the copy would
        // delete the primary instance's listener with it.
        if (!this.props.stackedDetail) {
            store.checkoutRefreshCS = this.refreshStuff;
            emitter.eventEmitter.subscribe(emitter.EventType.AB, 'ab', this.abForceUpdate);
        }
    }

    componentWillUnmount() {
        if (!this.props.stackedDetail) {
            emitter.eventEmitter.unsubscribe(emitter.EventType.AB, 'ab');
        }
    }

    @action
    saveEstPostal = async () => {
        store.uiCheckout.isLoadingEstimated = true;
        const success = await checkout('get-est-postal');
        store.uiCheckout.isLoadingEstimated = false;
        try {
            store.checkoutRefreshCS();
        } catch {
            const x = 1;
        }
        hideAllModals();
    };

    @computed get cartItemTotal() {
        return getCartItemTotal(store.checkout.cart.items);
    }

    @computed get freeShipping() {
        if (store.checkout.cart.pricing.discount_shipping > 0) {
            return store.checkout.cart.pricing.shipping + store.checkout.cart.pricing.production - store.checkout.cart.pricing.discount_shipping === 0;
        }
        return false;
    }

    @computed get showApplePay() {
        return (/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream) || /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    @computed get EstPostalCode() {
        return (store.checkout.ship.addr.postalCode.value.trim() === '' ? store.checkout.ship.estimatedPostalCode : store.checkout.ship.addr.postalCode.value).trim();
    }

    @computed get showEstBox() {
        return store.checkout.cart.pricing.isEstShipping && store.checkout.ship.estimatedPostalCode === '';
    }

    @computed get superSaverDiscount() {
        try {
            return isNaN(store.checkout.ship.shipMethod.SuperSaverDiscount) ? -1 : store.checkout.ship.shipMethod.SuperSaverDiscount;
        } catch {
            return 0;
        }
    }

    @computed get getVolumeDiscount(): number {
        try {
            let _total: number = 0;
            for (var i = 0; i < store.checkout.cart.items.length; i++) {
                _total += store.checkout.cart.items[i].VolumeDiscountAmt;
            }
            return _total;
        } catch {
            return 0;
        }
    }

    @action
    resetCart = async () => {
        store.initCheckout();
        store.uiCheckout.requireReset = true;
        store.moveStep(ICheckoutStep.cart);
        this.forceUpdate();
    };

    @action
    handleCheckoutTypeChange = async (e: ICheckoutType) => {
        if (store.checkout.force_normal_checkout) {
            store.checkout.CheckoutType = ICheckoutType.normal;
            store.moveStep(ICheckoutStep.checkout);
            return;
        }

        store.checkout.CheckoutType = e;
        store.checkout.force_normal_checkout = true;
        // if (store.checkout.CheckoutType === ICheckoutType.paypal_express || store.checkout.CheckoutType === ICheckoutType.stripe_express){
        //     store.checkout.force_normal_checkout = true;
        // }
        store.moveStep(ICheckoutStep.checkout);
    };

    @action
    reloadPayment = async () => {
        this.editingPromo = false;
        try {
            store.checkoutReloadPayment();
        } catch (err) {
            const x = 1;
        }
        this.forceUpdate();
    };

    @action
    reloadPaymentFailure = async () => {
        this.editingPromo = true;
        try {
            store.checkoutReloadPayment();
        } catch (err) {
            const x = 1;
        }
        this.forceUpdate();
    };

    @action
    refreshStuff = async () => {
        this.forceUpdate();
    };

    @action
    triggerFocus = (id: string): void => {
        setTimeout(() => {
            const element = document.querySelector(id) as HTMLElement;

            if (element) {
                element.focus();
            }
        }, 100);
    };

    render() {
        collapse.init();

        const collapseId = `cartSummaryCollapse${this.randInt.toString()}`;

        if (isCheckoutRedesign(store.checkout.isMyFrames)) {
            // The cart list owns the redesign's empty-cart message; the legacy summary box must not show next to it.
            if (!store.checkout.cart.items || store.checkout.cart.items.length < 1) {
                return null;
            }
            return (
                <SummaryRedesign
                    isMobile={this.props.isMobile}
                    detail={!!this.props.stackedDetail}
                    showEstBox={this.showEstBox}
                    freeShipping={this.freeShipping}
                    superSaverDiscount={this.superSaverDiscount}
                    onCheckoutTypeChange={this.handleCheckoutTypeChange}
                    onTriggerFocus={this.triggerFocus}
                    reloadPayment={this.reloadPayment}
                />
            );
        }

        return (
            <div
                className={this.props.isMobile ? 'cs cs-mobile' : 'cs'}
                style={{border: '1px solid #EAEAEA'}}
            >
                {this.props.isMobile && <MobileHeader />}
                <div>
                    <ModalPromo
                        reloadPaymentFailure={this.reloadPaymentFailure}
                        reloadPayment={this.reloadPayment}
                        modalId="modal-checkout-promo-cs"
                    />
                </div>

                <div className={this.props.isMobile && store.checkout.cstep > ICheckoutStep.cart ? 'toggle-content' : ''}>
                    <Header
                        isMobile={this.props.isMobile}
                        collapseId={collapseId}
                        hasItems={!!(store.checkout.cart.items && store.checkout.cart.items.length > 0)}
                    />

                    {store.checkout.cart.items && store.checkout.cart.items.length > 0 && (
                        <div
                            id={collapseId}
                            className={this.props.isMobile && store.checkout.cstep > ICheckoutStep.cart ? 'collapse' : ''}
                        >
                            <Totals
                                cartItemTotal={this.cartItemTotal}
                                showEstBox={this.showEstBox}
                                EstPostalCode={this.EstPostalCode}
                                freeShipping={this.freeShipping}
                                superSaverDiscount={this.superSaverDiscount}
                                volumeDiscount={this.getVolumeDiscount}
                            />

                            <Promo
                                editingPromo={this.editingPromo}
                                onTogglePromo={() => (this.editingPromo = !this.editingPromo)}
                                reloadPayment={this.reloadPayment}
                                reloadPaymentFailure={this.reloadPaymentFailure}
                            />

                            <TotalRow showEstBox={this.showEstBox} />

                            <CheckoutButtons
                                isMobile={this.props.isMobile}
                                onCheckoutTypeChange={this.handleCheckoutTypeChange}
                                onTriggerFocus={this.triggerFocus}
                            />

                            {(store.checkout.cstep > ICheckoutStep.cart || this.props.isMobile) && (
                                <CartItems
                                    id={this.props.isMobile ? 100 : 50}
                                    isMobile={this.props.isMobile}
                                />
                            )}

                            <div className="d-none d-md-block">
                                <Confidence />
                            </div>
                        </div>
                    )}

                    <PayLater isMobile={this.props.isMobile} />
                </div>

                <MobileFooter isMobile={this.props.isMobile} />
                <EmptyCart isMobile={this.props.isMobile} />
            </div>
        );
    }
}
export default CartSummaryComponent;
